import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography, IconButton, Box, CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { EvaluationCategoryData } from '../../../services/Evaluation.service';

const AddProspectModal = ({ open, onClose, onProspectAdded, onSave, isOfflineMode = false }) => {
  const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '' });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validate = async () => {
    const newErrors = {};

    const emailValue = formData.email.trim();
    if (emailValue) {
      if (!validateEmail(emailValue)) {
        newErrors.email = 'Please enter a valid email address.';
      } else if (!isOfflineMode) {
        try {
          const exists = await EvaluationCategoryData.checkEmailExists(emailValue);
          if (exists) {
            newErrors.email = 'A user with this email already exists.';
          }
        } catch {
          // If the network check fails, let save continue and allow the caller to queue locally.
        }
      }
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required.';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const isValid = await validate();
    if (!isValid) return;

    setIsSaving(true);
    try {
      const payload = {
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      };
      const result = await onSave(payload);
      setFormData({ email: '', firstName: '', lastName: '' });
      setErrors({});
      if (onProspectAdded) onProspectAdded({ ...payload, ...(result || {}) });
      onClose();
    } catch (error) {
      setErrors({ submit: error?.message || 'Failed to add prospect. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', firstName: '', lastName: '' });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableBackdropClick>
      <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1d428a', color: '#fff', padding: '12px 16px' }}>
        <Box display="flex" alignItems="center">
          <PersonAddIcon style={{ color: '#fff', marginRight: 8 }} />
          <Typography variant="subtitle1" style={{ fontWeight: 500, color: '#fff' }}>Add New Prospect</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} style={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {errors.submit && (
          <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
            {errors.submit}
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              variant="outlined"
              size="small"
              fullWidth
              required
              autoFocus
              value={formData.firstName}
              onChange={handleChange('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              variant="outlined"
              size="small"
              fullWidth
              required
              value={formData.lastName}
              onChange={handleChange('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              variant="outlined"
              size="small"
              fullWidth
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button
          onClick={handleSubmit}
          disabled={isSaving}
          style={{ textTransform: 'none' }}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : 'SAVE'}
        </Button>
        <Button onClick={handleClose} style={{ textTransform: 'none' }}>
          CANCEL
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProspectModal;
